import { useEffect, useRef } from "react";
import { motion, useAnimationControls, useMotionValue, useSpring } from "framer-motion";

function RadarWidget() {
  const controls = useAnimationControls();
  const radarRef = useRef(null);
  const pingX = useMotionValue(100);
  const pingY = useMotionValue(100);
  const smoothPingX = useSpring(pingX, { damping: 25, stiffness: 400 });
  const smoothPingY = useSpring(pingY, { damping: 25, stiffness: 400 });
  
  useEffect(() => {
    // Start the sweep rotation after radar elements are loaded
    const timer = setTimeout(() => {
      controls.start({ 
        rotate: 360,
        opacity: 1,
        transition: { 
          rotate: { duration: 6, repeat: Infinity, ease: "linear" },
          opacity: { duration: 0.5, ease: "easeInOut" }
        } 
      });
    }, 2800); // Wait for radar to finish loading
    
    return () => clearTimeout(timer);
  }, [controls]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      // Calculate mouse position relative to screen center
      const screenCenterX = window.innerWidth / 2;
      const screenCenterY = window.innerHeight / 2;
      
      const mouseX = e.clientX - screenCenterX;
      const mouseY = e.clientY - screenCenterY;
      
      // Scale mouse position to radar coordinates
      const maxScreenDistance = Math.min(window.innerWidth, window.innerHeight) / 2;
      const normalizedX = mouseX / maxScreenDistance;
      const normalizedY = mouseY / maxScreenDistance;
      
      // Map to SVG coordinates (center is 100,100, radius is ~90)
      const maxRadarRadius = 90;
      let radarX = 100 + normalizedX * maxRadarRadius;
      let radarY = 100 + normalizedY * maxRadarRadius;
      
      // Constrain to circular area
      const distanceFromCenter = Math.sqrt((radarX - 100) ** 2 + (radarY - 100) ** 2);
      if (distanceFromCenter > maxRadarRadius) {
        const angle = Math.atan2(radarY - 100, radarX - 100);
        radarX = 100 + Math.cos(angle) * maxRadarRadius;
        radarY = 100 + Math.sin(angle) * maxRadarRadius;
      }
      
      pingX.set(radarX);
      pingY.set(radarY);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [pingX, pingY]);

  return (
    <div ref={radarRef} className="relative w-24 xl:w-48 h-24 xl:h-48 flex-shrink-0 pointer-events-none select-none">
      <svg viewBox="0 0 200 200" className="absolute inset-0">
        {/* Crosshair lines */}
        <motion.line 
          x1="0" 
          y1="100" 
          x2="200" 
          y2="100" 
          stroke="white"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.8, delay: 1.5, ease: "easeInOut" }}
        />
        <motion.line 
          x1="100" 
          y1="0" 
          x2="100" 
          y2="200" 
          stroke="white"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.8, delay: 1.7, ease: "easeInOut" }}
        />
        
        {/* Concentric circles from center out */}
        <motion.circle 
          cx="100" 
          cy="100" 
          r="25" 
          stroke="white" 
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
          fill="none"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.6, delay: 2.0, ease: "easeInOut" }}
        />
        <motion.circle 
          cx="100" 
          cy="100" 
          r="50" 
          stroke="white" 
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
          fill="none"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.6, delay: 2.2, ease: "easeInOut" }}
        />
        <motion.circle 
          cx="100" 
          cy="100" 
          r="75" 
          stroke="white" 
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
          fill="none"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.6, delay: 2.4, ease: "easeInOut" }}
        />
        <motion.circle 
          cx="100" 
          cy="100" 
          r="100" 
          stroke="white" 
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
          fill="none"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.6, delay: 2.6, ease: "easeInOut" }}
        />
        {/* Mouse-following ping */}
        <motion.g
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 3, duration: 0.5 }}
        >
          {/* Ping dot */}
          <motion.circle
            r="2"
            fill="white"
            style={{ cx: smoothPingX, cy: smoothPingY }}
            animate={{ 
              scale: [1, 2, 1],
              opacity: [0.8, 1, 0.8]
            }}
            transition={{ 
              duration: 2, 
              repeat: Infinity, 
              ease: "easeInOut" 
            }}
          />
          {/* Ping ripple */}
          <motion.circle
            r="8"
            stroke="white"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
            fill="none"
            style={{ cx: smoothPingX, cy: smoothPingY }}
            animate={{ 
              scale: [0, 4],
              opacity: [1, 0]
            }}
            transition={{ 
              duration: 2, 
              repeat: Infinity, 
              ease: "easeInOut" 
            }}
          />
        </motion.g>
      </svg>
      
      {/* Radar sweep */}
      <motion.img
        src="/images/radar-sweep.png"
        alt=""
        className="absolute inset-0 w-full h-full object-contain"
        style={{ transformOrigin: "center" }}
        initial={{ opacity: 0, rotate: 0 }}
        animate={controls}
      />
    </div>
  );
}

export default RadarWidget;